import { Injectable } from '@angular/core';
import { FormControl, FormGroup, Validators, ValidatorFn, AbstractControl } from '@angular/forms';

import { FormField } from '../models/form-field';
import { FormFieldDropdown } from '../models/form-field-dropdown';
import { FormFieldTextBox } from '../models/form-field-textbox';
import { FormFieldTextArea } from '../models/form-field-textarea';
import { FormFieldRadio } from '../models/form-field-radio';
import { VolvoApiService } from './volvo-api.service';
import { FormFieldCheckbox } from '../models/form-field-checkbox';
import { Observable } from 'rxjs';

@Injectable()
export class FormControlService {

  private localStrings$: Observable<any>;

  constructor(
    private volvoApi: VolvoApiService
  ) {
    this.localStrings$ = volvoApi.getPromotionLocalizationString();
  }

  /**
   * Generate a form group from an array of form field rows
   * @param formRowFields
   */
  toFormGroup(formRowFields: Array<Array<FormField<any>>>) {
    const group: any = {};

    formRowFields.forEach(formRow => {
      if (formRow instanceof Array) {
        formRow.forEach(f => {
          group[f.key] = new FormControl(f.value || '', f.validators || null);
        });
      }
    });
    return new FormGroup(group);
  }

  /**
   *
   * @param name
   * @param cssClasses
   * @param options
   * @param validators
   * @param errorMessageRequired
   * @param placeholder
   * @param desriptionText
   */
  createDropDownField(
      name: string,
      cssClasses: any,
      options: any,
      validators: any,
      errorMessageRequired: any,
      placeholder: any,
      desriptionText: string = ""): FormFieldDropdown {
    const field = new FormFieldDropdown({
      key: name,
      order: 0
    });
    if (cssClasses) { field.cssClasses = cssClasses; }
    if (options) { field.options = options; }
    if (validators) { field.validators = validators; }
    if (errorMessageRequired) { field.errorMessageRequired = errorMessageRequired; }
    if (placeholder) { field.placeholder = placeholder; }
    if (desriptionText) { field.desriptionText = desriptionText }

    return field;
  }

  /**
   *
   * @param name
   * @param label
   * @param cssClasses
   * @param options
   * @param validators
   * @param errorMessageRequired
   * @param desriptionText
   */
  createRadioField(
      name: string,
      label: string,
      defaultValue: string,
      cssClasses: any,
      options: any,
      validators: any,
      errorMessageRequired: any,
      desriptionText: string = ""): FormFieldRadio {
    const field = new FormFieldRadio({
      label: label,
      key: name,
      order: 0,
    }, defaultValue);
    if (cssClasses) { field.cssClasses = cssClasses; }
    if (options) { field.options = options; }
    if (validators) { field.validators = validators; }
    if (errorMessageRequired) { field.errorMessageRequired = errorMessageRequired; }
    if (desriptionText) { field.desriptionText = desriptionText }
    return field;
  }

  /**
   *
   * @param name
   * @param placeholder
   * @param order
   * @param cssClasses
   * @param errorMessageRequired
   * @param errorMessageValid
   * @param validators
   * @param desriptionText
   */
  createTextBox(
      name: string,
      placeholder: string,
      order: any,
      cssClasses: any,
      maxlength: number,
      errorMessageRequired: any,
      errorMessageValid: any,
      validators: any,
      desriptionText: string = ""): FormFieldTextBox {
    const field = new FormFieldTextBox({
      key: name,
      placeholder: placeholder,
      maxlength: maxlength,
    });
    if (order) { field.order = order; }
    if (cssClasses) { field.cssClasses = cssClasses; }
    if (errorMessageRequired) { field.errorMessageRequired = errorMessageRequired; }
    if (errorMessageValid) { field.errorMessageValid = errorMessageValid; }
    if (validators) { field.validators = validators; }
    if (desriptionText) { field.desriptionText = desriptionText }
    return field;
  }


  /**
   * Generate array of form field rows from xml data
   * @param formFieldsRawData xml data returned from web service
   * @param isExtraField
   */
  generateFormRows(formFieldsRawData: any, isExtraField: any): Array<any> {

    const formRows: Array<any>[] = [];

    const fields = formFieldsRawData['form']['field'];
    // console.log(fields)
    const inputOptions = null;

    fields.forEach(f => {
      const formRowFields: Array<any>[] = [];
      const newField = this.generateFormField(f, isExtraField);
      if (newField) {
        formRowFields.push(newField);
        formRows.push(formRowFields);
      }
    });

    return formRows;
  }

  /**
   * Generate form field from xml data
   * @param f field element
   * @param isExtraField
   */
  generateFormField(f: any, isExtraField: any): any {
    // console.log(f);
    let newField: any;

    if (f.properties[0].fieldcaption) {
      const fieldLabel = f.properties[0].fieldcaption[0]['_'] ? f.properties[0].fieldcaption[0]['_'] : f.properties[0].fieldcaption[0];
      const fieldType = f.settings[0].controlname[0];

      const fieldHint = f.properties[0].validationerrormessage ?
        (f.properties[0].validationerrormessage[0]['_'] ? f.properties[0].validationerrormessage[0]['_']
          : f.properties[0].validationerrormessage[0])
        : null;
      const invalidMsg = f.rules ? f.rules[0].rule[0].errormsg[0] : null;

      const fieldID = f.column[0];

      if (fieldID) {

        if (fieldType === 'TextBoxControl') {
          newField = new FormFieldTextBox({});
        } else if (fieldType === 'TextAreaControl') {
          newField = new FormFieldTextArea({ rows: 7 });
        } else if (fieldType === 'DropDownListControl') {
          newField = new FormFieldDropdown({});
        } else if (fieldType === 'RadioButtonsControl') {
          newField = new FormFieldRadio({}, null);
        } else if (fieldType === 'CheckBoxControl') {
          const fieldExplanation = f.properties[0].explanationtext[0] || '';
          const defaultValue = f.properties[0].defaultvalue;
          newField = new FormFieldCheckbox({label: fieldExplanation}, defaultValue);
        } else if (fieldType === 'emailinput') {
          newField = new FormFieldTextBox({});
        }
        if (newField) {
          newField.key = fieldID;
          newField.placeholder = fieldLabel;

          // validation
          if (fieldHint) {
            newField.validators = Validators.required;
            newField.errorMessageRequired = fieldHint;
            if (fieldType === 'emailinput') {
              newField.validators = [Validators.required, Validators.email];
            }
          }
          if (isExtraField) {
            newField.isExtraField = true;
          }
          if (invalidMsg) {
            newField.errorMessageValid = invalidMsg;
          }

          return newField;
        } else {
          // hidden fields?
        }
      } else {
        // hidden fields?
      }
    }
  }


  /**
   * Generate basic info fields (name, address, city) in an array of form fields
   * @param localStrings
   */
  getBasicInfoFields(localStrings: Array<any>, capitalize?: boolean, format?: string): Array<any> {
    const rowfields: FormField<any>[][] = [
      [
        new FormFieldTextBox({
          key: capitalize ? 'FirstName' : 'firstname',
          placeholder: format === 'redsox' ? localStrings['AV.Promotions.lb.firstname.RedSox'] : localStrings['AV.Promotions.lb.firstname'],
          cssClasses: 'column medium-6 form-control-wrapper required',
          order: 1,
          errorMessageRequired: localStrings['AV.Promotions.lb.validation.firstname'],
          validators: Validators.required
        }),
        new FormFieldTextBox({
          key: capitalize ? 'LastName' : 'lastname',
          placeholder: format === 'redsox' ? localStrings['AV.Promotions.lb.lastname.RedSox'] : localStrings['AV.Promotions.lb.lastname'],
          cssClasses: 'column medium-6 form-control-wrapper required',
          order: 2,
          errorMessageRequired: localStrings['AV.Promotions.lb.validation.lastname'],
          validators: Validators.required
        })
      ],
      [
        new FormFieldTextBox({
          key: capitalize ? 'Address' : 'address',
          placeholder: localStrings['AV.Promotions.lb.address'],
          order: 1,
          errorMessageRequired: localStrings['AV.Promotions.lb.validation.address'],
          validators: Validators.required
        })
      ],
      [
        new FormFieldTextBox({
          key: capitalize ? 'City' : 'city',
          placeholder: localStrings['AV.Promotions.lb.city'],
          order: 1,
          maxlength: 40,
          errorMessageRequired: localStrings['AV.Promotions.lb.validation.city'],
          validators: Validators.required
        })
      ]
    ];
    return rowfields;
  }

 /**
   * Generate Fleet Company and VIN fields in an array of form fields
   * @param localStrings object for labels and validation messages
   */
  getFleetPinFields(localStrings: Array<any>) : Array<any> {
    const rowfields: FormField<any>[][] = [
      [
        this.createTextBox(
          'fleetDriveCompany',
          localStrings['AV.Promotions.lb.fleetDriveCompany'],
          1,
          'form-control-wrapper required',
          null,
          localStrings['AV.Promotions.lb.validation.fleetDriveCompany'],
          null,
          Validators.required
        )
      ],
      [
        this.createTextBox(
          'fleetVin',
          localStrings['AV.Promotions.lb.fleetVin'],
          1,
          '',
          null,
          localStrings['AV.Promotions.lb.validation.fleetVin.required'],
          localStrings['AV.Promotions.lb.validation.fleetVin.invalid'],
          [
            Validators.required,
            // the field should be exactly 6 characters
            // can include numbers and letters
            this.patternValidator(/^[A-Za-z0-9]{6,6}$/)
          ]
        )
      ]
    ];
    return rowfields;
  }

/**
   * Generate RedSox Ticket Account Number an array of form fields
   * @param localStrings object for labels and validation messages
   */
getRedSoxPinFields(localStrings: Array<any>) : Array<any> {
  const rowfields: FormField<any>[][] = [
    [
      this.createTextBox(
        'redSoxTicketAccountNumber',
        localStrings['AV.Promotions.lb.redSoxTicketAccountNumber'],
        1,
        'form-control-wrapper required',
        null,
        localStrings['AV.Promotions.lb.validation.redSoxTicketAccountNumber'],
        null,
        Validators.required
      )
    ]
  ];
  return rowfields;
}

  /**
   *
   * @param r
   * @param keySelector
   * @param placeholder
   */
  createOptionsObj(r: Array<any>, keySelector: string, placeholder: string | any): Array<any> {
    const options = [];

    if (placeholder) {
      const optPH = {
        key: '',
        value: placeholder,
        selected: true,
        disabled: true
      };
      options.push(optPH);
    }


    Array.prototype.push.apply(options,
      r.map(obj => {
        const optObj = {};
        optObj['key'] = obj[keySelector];
        optObj['value'] = obj.Name;
        return optObj;
      })
    );

    return options;
  }

  /**
   * create regexp validator
   * @param regexp
   */
  patternValidator(regexp: RegExp): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } => {
      const value = control.value;
      if (value === '') {
        return null;
      }
      return !regexp.test(value) ? { 'patternInvalid': { regexp } } : null;
    };
  }

  /** touch form controls to trigger validation */
  validateForm(form: FormGroup): boolean {
    if (form.valid) {
      return true;
    } else {
      for (const control in form.controls) {
        if (form.controls.hasOwnProperty(control)) {
          const element = form.controls[control];
          if (element instanceof FormControl) {
            element.markAsTouched();
          }
        }
      }
      return false;
    }
  }

}
